import React, { useState, useEffect } from 'react';

const galleryItems = [
  {
    id: 1,
    title: 'Into the Trees',
    category: 'landscapes',
    aspect: 'landscape',
    src: '/assets/portfolio/boardwalk-trail.jpg',
    caption: 'A winding boardwalk trail in late-afternoon light.',
  },
  {
    id: 2,
    title: 'Black-Eyed Susans',
    category: 'nature',
    aspect: 'portrait',
    src: '/assets/portfolio/black-eyed-susans.jpg',
    caption: 'A late-summer field of black-eyed susans.',
  },
  {
    id: 3,
    title: 'Desk Supervisor',
    category: 'pets',
    aspect: 'landscape',
    src: '/assets/portfolio/cat-at-desk.jpg',
    caption: 'Low, directional window light on a tabby keeping watch over the desk.',
  },
  {
    id: 4,
    title: 'Against the Blue',
    category: 'nature',
    aspect: 'portrait',
    src: '/assets/portfolio/reed-blue-sky.jpg',
    caption: 'A single plume of reed grass against a clear sky.',
  },
  {
    id: 5,
    title: 'Lakeshore Afternoon',
    category: 'landscapes',
    aspect: 'landscape',
    src: '/assets/portfolio/lakeshore-afternoon.jpg',
    caption: 'Still water and a quiet shoreline under soft afternoon cloud.',
  },
  {
    id: 6,
    title: 'Caught Mid-Nap',
    category: 'pets',
    aspect: 'portrait',
    src: '/assets/portfolio/cat-on-chair.jpg',
    caption: 'A relaxed tabby draped over the arm of a chair.',
  },
];

const categories = [
  { key: 'all', label: 'All Work' },
  { key: 'landscapes', label: 'Landscapes' },
  { key: 'nature', label: 'Nature' },
  { key: 'pets', label: 'Pet Portraits' },
];

const categoryLabel = (key) => categories.find((cat) => cat.key === key)?.label ?? key;

const Gallery = () => {
  const [filter, setFilter] = useState('all');
  const [activePhoto, setActivePhoto] = useState(null);

  const filteredItems =
    filter === 'all'
      ? galleryItems
      : galleryItems.filter((item) => item.category === filter);

  // Keyboard navigation for the lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!activePhoto) return;
      if (e.key === 'Escape') setActivePhoto(null);
      if (e.key === 'ArrowRight') {
        const currentIndex = filteredItems.findIndex((item) => item.id === activePhoto.id);
        const nextIndex = (currentIndex + 1) % filteredItems.length;
        setActivePhoto(filteredItems[nextIndex]);
      }
      if (e.key === 'ArrowLeft') {
        const currentIndex = filteredItems.findIndex((item) => item.id === activePhoto.id);
        const prevIndex = (currentIndex - 1 + filteredItems.length) % filteredItems.length;
        setActivePhoto(filteredItems[prevIndex]);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activePhoto, filteredItems]);

  return (
    <section id="gallery" className="section gallery-section" aria-labelledby="gallery-title">
      <div className="container">
        <header className="section__header">
          <span className="section__tagline">Behind the Lens</span>
          <h2 id="gallery-title" className="section__title">Gallery</h2>
          <p className="section__description">
            Personal work: landscapes, nature, and portraits of the animals I live with.
          </p>
        </header>

        {/* Filter Pills */}
        <div className="gallery__filters" role="tablist" aria-label="Gallery categories">
          {categories.map((cat) => (
            <button
              key={cat.key}
              role="tab"
              aria-selected={filter === cat.key}
              className={`gallery__filter-btn ${filter === cat.key ? 'gallery__filter-btn--active' : ''}`}
              onClick={() => setFilter(cat.key)}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Masonry Grid */}
        <div className="gallery__grid">
          {filteredItems.map((item) => (
            <article
              key={item.id}
              className={`gallery__card gallery__card--${item.aspect}`}
              onClick={() => setActivePhoto(item)}
            >
              <img
                src={item.src}
                alt={item.title}
                loading="lazy"
                className="gallery__image"
              />
              <div className="gallery__overlay">
                <div className="gallery__overlay-content">
                  <span className="gallery__badge">{categoryLabel(item.category)}</span>
                  <h3 className="gallery__card-title">{item.title}</h3>
                  <span className="gallery__view-hint">View Frame ↗</span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      {activePhoto && (
        <div className="lightbox" role="dialog" aria-modal="true" aria-label={activePhoto.title}>
          <div className="lightbox__backdrop" onClick={() => setActivePhoto(null)} />
          <div className="lightbox__dialog">
            <button
              className="lightbox__close"
              onClick={() => setActivePhoto(null)}
              aria-label="Close image lightbox"
            >
              ✕
            </button>
            <div className="lightbox__figure">
              <img
                src={activePhoto.src}
                alt={activePhoto.title}
                className="lightbox__img"
              />
              <div className="lightbox__meta">
                <div className="lightbox__info">
                  <span className="lightbox__category">{categoryLabel(activePhoto.category)}</span>
                  <h3 className="lightbox__title">{activePhoto.title}</h3>
                  <p className="lightbox__caption">{activePhoto.caption}</p>
                </div>
                <a href="#contact" className="btn btn--primary" onClick={() => setActivePhoto(null)}>
                  Book a Shoot
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;